/**
 * Intervalos e navegação do calendário — funções puras, sem React.
 *
 * Semana começa à segunda (norma em Portugal). Todos os intervalos são
 * `[start, end)`: o fim é EXCLUSIVO (meia-noite do dia seguinte ao último dia
 * visível), o que evita o erro clássico de "23:59:59.999" e torna a
 * comparação de sobreposição uma simples `start < fim && end > início`.
 */

import {
  addDays,
  addMonths,
  differenceInCalendarDays,
  endOfMonth,
  format,
  getISOWeek,
  isSameMonth,
  isSameYear,
  startOfDay,
  startOfMonth,
  startOfWeek,
} from "date-fns";
import { pt } from "date-fns/locale";

import type { CalendarView } from "../../../lib/calendar/calendar-types";

const WEEK_STARTS_ON = 1 as const;

/** Dias que a vista Agenda percorre a partir do cursor. */
const AGENDA_SPAN_DAYS = 30;

export interface DateRange {
  start: Date;
  /** Exclusivo. */
  end: Date;
}

const weekStart = (d: Date): Date => startOfWeek(d, { weekStartsOn: WEEK_STARTS_ON });

/** Dias consecutivos de `start` (inclusive) durante `count` dias. */
export function daysFrom(start: Date, count: number): Date[] {
  const base = startOfDay(start);
  return Array.from({ length: count }, (_, i) => addDays(base, i));
}

/** Os 7 dias da semana que contém `d`. */
export const weekDays = (d: Date): Date[] => daysFrom(weekStart(d), 7);

/** Semanas (linhas) que cobrem o mês de `d`, completas de segunda a domingo. */
export function monthWeeks(d: Date): Date[][] {
  const first = weekStart(startOfMonth(d));
  const last = endOfMonth(d);
  const total = Math.ceil((differenceInCalendarDays(last, first) + 1) / 7);
  return Array.from({ length: total }, (_, w) => daysFrom(addDays(first, w * 7), 7));
}

/** O intervalo de datas que a vista precisa de desenhar. */
export function visibleRange(view: CalendarView, cursor: Date): DateRange {
  const day = startOfDay(cursor);
  switch (view) {
    case "day":
      return { start: day, end: addDays(day, 1) };
    case "week": {
      const s = weekStart(day);
      return { start: s, end: addDays(s, 7) };
    }
    case "month": {
      const weeks = monthWeeks(day);
      const s = weeks[0][0];
      return { start: s, end: addDays(s, weeks.length * 7) };
    }
    case "agenda":
      return { start: day, end: addDays(day, AGENDA_SPAN_DAYS) };
  }
}

/** Recua/avança o cursor uma "página" da vista. */
export function shiftCursor(view: CalendarView, cursor: Date, direction: 1 | -1): Date {
  switch (view) {
    case "day":
      return addDays(cursor, direction);
    case "week":
      return addDays(cursor, 7 * direction);
    case "month":
      return addMonths(cursor, direction);
    case "agenda":
      return addDays(cursor, AGENDA_SPAN_DAYS * direction);
  }
}

const capitalize = (s: string) => s.charAt(0).toUpperCase() + s.slice(1);

/** O título do período, em português: "Setembro de 2026", "28 set – 4 out 2026". */
export function rangeTitle(view: CalendarView, cursor: Date): string {
  if (view === "month") return capitalize(format(cursor, "MMMM 'de' yyyy", { locale: pt }));
  if (view === "day") return capitalize(format(cursor, "EEEE, d 'de' MMMM 'de' yyyy", { locale: pt }));

  const { start, end } = visibleRange(view, cursor);
  const last = addDays(end, -1);
  const from = format(start, isSameMonth(start, last) ? "d" : "d MMM", { locale: pt });
  const to = format(last, "d MMM", { locale: pt });
  const year = isSameYear(start, last) ? format(last, "yyyy") : `${format(start, "yyyy")} – ${format(last, "yyyy")}`;
  return `${from} – ${to} ${year}`;
}

export const isoWeekNumber = (d: Date): number => getISOWeek(d);

/** "yyyy-MM-dd" no fuso local — nunca `toISOString()`, que salta um dia entre 00:00 e 01:00 em Lisboa. */
export const toISODate = (d: Date): string => format(d, "yyyy-MM-dd");

/** "HH:mm" no fuso local. */
export const toHHmm = (d: Date): string => format(d, "HH:mm");

/** Interpreta "yyyy-MM-dd" como data LOCAL (o `new Date("2026-09-30")` seria UTC). */
export function parseISODate(iso: string): Date {
  const [y, m, d] = iso.slice(0, 10).split("-").map(Number);
  return new Date(y, m - 1, d);
}

/** "HH:mm[:ss]" → minutos desde a meia-noite; `null` se vazio. */
export function timeToMinutes(time: string | null | undefined): number | null {
  if (!time) return null;
  const [h, m] = time.slice(0, 5).split(":").map(Number);
  if (Number.isNaN(h) || Number.isNaN(m)) return null;
  return h * 60 + m;
}

export function minutesToTime(minutes: number): string {
  const clamped = Math.max(0, Math.min(24 * 60 - 1, Math.round(minutes)));
  const h = String(Math.floor(clamped / 60)).padStart(2, "0");
  const m = String(clamped % 60).padStart(2, "0");
  return `${h}:${m}`;
}
